import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Input, Empty, Skeleton, Tooltip } from "antd";
import { SearchOutlined, EditOutlined, GlobalOutlined, ClearOutlined } from "@ant-design/icons";

import { fetchGlobalTemplates } from "../cms/services/globalTemplateService";
import { isSuperAdmin } from "../cms/utils/role";
import AccessDenied from "../AccessDenied";

/**
 * Global Template Manager — the system templates (subscription, auth, wallet …).
 *
 * Deliberately a separate screen from the lead / customer Template Manager: only
 * a superadmin may touch these, and they save through their own endpoint. There
 * is no "create" here — global templates are seeded by the backend, this screen
 * only edits the copy.
 */

const CONTEXT_STYLE = {
  lead:         { bg: "#e0f2fe", color: "#0369a1", border: "#bae6fd" },
  customer:     { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  subscription: { bg: "#eef2ff", color: "#4338ca", border: "#c7d2fe" },
  auth:         { bg: "#fef3c7", color: "#b45309", border: "#fde68a" },
  wallet:       { bg: "#fae8ff", color: "#a21caf", border: "#f5d0fe" },
};

const contextChip = (ctx) => {
  const key = String(ctx || "").trim().toLowerCase();
  const s = CONTEXT_STYLE[key] || { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" };
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, textTransform: "capitalize", whiteSpace: "nowrap" }}>
      {key || "—"}
    </span>
  );
};

const CHUNK = 20;

/* template_type as the backend stores it. Transactional mail may always go out;
   Promotional needs consent, which is why the two are kept apart. */
const TYPE_OPTIONS = [
  { value: "1", label: "General",       color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
  { value: "2", label: "Notification",  color: "#0369a1", bg: "#e0f2fe", border: "#bae6fd" },
  { value: "3", label: "Marketing",     color: "#b45309", bg: "#fef3c7", border: "#fde68a" },
  { value: "4", label: "Service",       color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
];

const typeChip = (val) => {
  const t = TYPE_OPTIONS.find((o) => o.value === String(val ?? "").trim());
  if (!t) return <span style={{ fontSize: 12, color: "#9ca3af" }}>—</span>;
  return (
    <span style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
      {t.label}
    </span>
  );
};

const th = { padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap", borderBottom: "2px solid #eef2ff", background: "#fafbff" };
const td = { padding: "14px 16px", borderBottom: "1px solid #f5f7ff", fontSize: 13, color: "#374151" };

export default function GlobalTemplatesPage() {
  const navigate = useNavigate();
  const allowed = isSuperAdmin();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [contextFilter, setContextFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  /* Every context seen so far. Building the chips from the CURRENT response made
     them disappear the moment one was picked — the response then holds only that
     context — so the user had to go back to "All" just to see the others. */
  const [knownContexts, setKnownContexts] = useState([]);
  /* The endpoint hands back the whole set in one go, so the scrolling is done on
     the rendered rows: a chunk at a time, more as the sentinel comes into view.
     52 rows is fine today, but a few hundred would make the page crawl. */
  const [visibleCount, setVisibleCount] = useState(CHUNK);
  const sentinelRef = useRef(null);

  // Typing shouldn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!allowed) return;
    let alive = true;
    setLoading(true);
    fetchGlobalTemplates({
      ...(debounced ? { search: debounced } : {}),
      ...(contextFilter !== "all" ? { context_type: contextFilter } : {}),
      ...(typeFilter !== "all" ? { template_type: typeFilter } : {}),
    })
      .then((list) => {
        if (!alive) return;
        setTemplates(list);
        setKnownContexts((prev) => {
          const merged = new Set(prev);
          list.forEach((t) => {
            const c = String(t.context_type || "").trim().toLowerCase();
            if (c) merged.add(c);
          });
          return Array.from(merged).sort();
        });
      })
      .catch((err) => { if (alive) { toast.error(err.message); setTemplates([]); } })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [debounced, contextFilter, typeFilter, allowed]);

  /* The request already asks the API to filter; this repeats the match on what
     came back, so the box keeps working whatever the endpoint supports. */
  const q = debounced.trim().toLowerCase();
  const rows = templates.filter((t) => {
    const matchSearch =
      !q ||
      String(t.template_name || "").toLowerCase().includes(q) ||
      String(t.subject || "").toLowerCase().includes(q);
    const ctx = String(t.context_type || "").trim().toLowerCase();
    const matchCtx = contextFilter === "all" || ctx === contextFilter;
    const matchType = typeFilter === "all" || String(t.template_type ?? "").trim() === typeFilter;
    return matchSearch && matchCtx && matchType;
  });

  // Filtering changes what is listed, so the window starts over.
  useEffect(() => { setVisibleCount(CHUNK); }, [debounced, contextFilter, typeFilter]);

  // Anything narrowing the list right now — drives the Reset button.
  const filtersActive = Boolean(search) || contextFilter !== "all" || typeFilter !== "all";
  const resetFilters = () => {
    setSearch("");
    setContextFilter("all");
    setTypeFilter("all");
  };

  const hasMore = visibleCount < rows.length;
  const loadMore = () => setVisibleCount((c) => c + CHUNK);

  /* Two triggers on purpose. IntersectionObserver is the clean one, but this app
     scrolls an inner layout container, and in that case the sentinel can sit in
     view without the observer ever firing — so a plain scroll listener backs it
     up. The footer is clickable too, which makes it work no matter what. */
  useEffect(() => {
    if (!hasMore) return;
    const node = sentinelRef.current;

    const io = node
      ? new IntersectionObserver(
          (entries) => { if (entries[0]?.isIntersecting) loadMore(); },
          { rootMargin: "300px" },
        )
      : null;
    io?.observe(node);

    let ticking = false;
    const onScroll = () => {
      if (ticking || !sentinelRef.current) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const top = sentinelRef.current?.getBoundingClientRect().top ?? Infinity;
        if (top - 300 <= window.innerHeight) loadMore();
      });
    };
    // `true` — catches scrolling on any ancestor container, not just the window.
    window.addEventListener("scroll", onScroll, true);
    onScroll();   // already at the bottom when the filter shrinks the list

    return () => {
      io?.disconnect();
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [hasMore, visibleCount, rows.length]);

  // Every hook has run by now, so the role gate can bail out safely.
  if (!allowed) return <AccessDenied />;

  /* Chips come from every context seen so far, not just the current response —
     a new backend context still needs no code change, but picking one never hides
     the rest. */
  const contexts = knownContexts;

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* ── Header ── */}
          <div style={{ background: "linear-gradient(135deg,#4338ca 0%,#312e81 100%)", borderRadius: 16, padding: "26px 30px", marginBottom: 22, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, boxShadow: "0 8px 30px rgba(67,56,202,0.25)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 52, height: 52, background: "rgba(255,255,255,0.18)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 22 }}>
                <GlobalOutlined />
              </div>
              <div>
                <h4 style={{ color: "#fff", margin: 0, fontSize: 22, fontWeight: 700 }}>Global Template Manager</h4>
                <p style={{ color: "rgba(255,255,255,0.82)", margin: 0, fontSize: 13 }}>
                  System templates — subscription, auth, wallet and more.
                </p>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.16)", padding: "9px 16px", borderRadius: 10, fontSize: 13, color: "#fff", display: "flex", gap: 7 }}>
              <Link to="/cms/templates" style={{ color: "#fff", textDecoration: "none" }}>Templates</Link>
              <span style={{ opacity: 0.55 }}>›</span>
              <span style={{ fontWeight: 600 }}>Global</span>
            </div>
          </div>

          {/* ── Toolbar ── */}
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #eef2ff", boxShadow: "0 2px 14px rgba(0,0,0,0.05)", padding: "16px 20px", marginBottom: 18, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <Input
              prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
              placeholder="Search by name or subject…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ maxWidth: 320, borderRadius: 9, height: 38 }}
            />
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["all", ...contexts].map((c) => {
                const active = contextFilter === c;
                return (
                  <button
                    key={c}
                    onClick={() => setContextFilter(c)}
                    style={{
                      padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      textTransform: "capitalize",
                      border: `1px solid ${active ? "#4338ca" : "#e5e7eb"}`,
                      background: active ? "#4338ca" : "#fff",
                      color: active ? "#fff" : "#6b7280",
                    }}
                  >
                    {c === "all" ? "All" : c}
                  </button>
                );
              })}
            </div>
            {filtersActive && (
              <button
                onClick={resetFilters}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", color: "#4338ca", border: "1px solid #c7d2fe", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
              >
                <ClearOutlined /> Reset filters
              </button>
            )}
            <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
              {loading ? "Loading…" : `${rows.length} template${rows.length !== 1 ? "s" : ""}`}
            </span>

            {/* Type filter — its own row so the two dimensions stay readable */}
            <div style={{ flexBasis: "100%", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", borderTop: "1px solid #f1f5f9", paddingTop: 12, marginTop: 2 }}>
              <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>Type:</span>
              {[{ value: "all", label: "All" }, ...TYPE_OPTIONS].map((opt) => {
                const active = typeFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setTypeFilter(opt.value)}
                    style={{
                      padding: "5px 13px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      border: `1px solid ${active ? "#4338ca" : "#e5e7eb"}`,
                      background: active ? "#4338ca" : "#fff",
                      color: active ? "#fff" : "#6b7280",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Table ── */}
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #eef2ff", boxShadow: "0 2px 14px rgba(0,0,0,0.05)", overflow: "hidden" }}>
            {loading ? (
              <div style={{ padding: 22 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} active paragraph={{ rows: 1 }} style={{ marginBottom: 12 }} />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div style={{ padding: "60px 20px", textAlign: "center" }}>
                <Empty description={<span style={{ color: "#9ca3af" }}>No global templates found</span>} />
                {filtersActive && (
                  <button
                    onClick={resetFilters}
                    style={{ marginTop: 14, background: "#4338ca", color: "#fff", border: "none", borderRadius: 9, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                  >
                    Reset filters
                  </button>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: 60 }}>#</th>
                      <th style={th}>Template Name</th>
                      <th style={th}>Context</th>
                      <th style={th}>Type</th>
                      <th style={th}>Subject</th>
                      <th style={th}>Status</th>
                      <th style={{ ...th, textAlign: "center" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, visibleCount).map((t, i) => (
                      <tr key={t.id ?? i}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#fafbff")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                      >
                        <td style={{ ...td, color: "#9ca3af", fontWeight: 600 }}>{i + 1}</td>
                        <td style={{ ...td, fontWeight: 600, color: "#1f2937" }}>{t.template_name || "Untitled"}</td>
                        <td style={td}>{contextChip(t.context_type)}</td>
                        <td style={td}>{typeChip(t.template_type)}</td>
                        <td style={{ ...td, maxWidth: 320 }}>
                          <span style={{ display: "inline-block", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#6b7280" }}>
                            {t.subject || "—"}
                          </span>
                        </td>
                        <td style={td}>
                          {String(t.status) === "active" ? (
                            <span style={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>Active</span>
                          ) : (
                            <span style={{ background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>Inactive</span>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: "center" }}>
                          <Tooltip title="Edit global template">
                            <button
                              onClick={() => navigate(`/cms/templates/global/${t.id}/edit`)}
                              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe", borderRadius: 8, padding: "5px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                            >
                              <EditOutlined /> Edit
                            </button>
                          </Tooltip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Infinite scroll sentinel — crossing it renders the next chunk. */}
                <div
                  ref={sentinelRef}
                  onClick={hasMore ? loadMore : undefined}
                  style={{ padding: "14px 16px", textAlign: "center", background: "#fafbff", borderTop: "1px solid #f5f7ff", cursor: hasMore ? "pointer" : "default" }}
                >
                  {hasMore ? (
                    <span style={{ fontSize: 13, color: "#4338ca", fontWeight: 600 }}>
                      Showing {visibleCount} of {rows.length} — scroll or click to load more
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: "#9ca3af" }}>
                      All {rows.length} template{rows.length !== 1 ? "s" : ""} loaded
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
